import { createTheme } from "@mui/material/styles";

// Web Module Color Palette
const colors = {
    // Primary Colors
    primary: {
        main: "#FFDA61", // Vibrant Yellow (Brand color)
        dark: "#E5C457", // Darker shade of yellow for hover states
        light: "#FFF0C1", // Lighter shade of yellow for backgrounds
        contrastText: "#000000", // Black text on yellow background
    },
    secondary: {
        main: "#000000", // Black
        light: "#333333", // Dark Gray
        dark: "#000000", // Black (same as main for consistency)
        contrastText: "#FFFFFF", // White text on black background
    },
    // Base Colors
    text: {
        primary: "#000000", // Black text for important content
        secondary: "#333333", // Dark gray text for regular content
        disabled: "#9E9E9E", // Medium gray for disabled text
    },
    background: {
        default: "#FFFFFF", // White for main background
        paper: "#F5F5F5", // Light gray for cards and paper elements
        dark: "#333333", // Dark gray for sections requiring contrast
    },
    // Accent Colors
    accent: {
        orange: "#FFA500", // Orange for CTA buttons and highlights
        lightBlue: "#ADD8E6", // Light blue for trust elements and soft backgrounds
    },
    // Additional UI Colors
    divider: "#E0E0E0", // Light gray for dividers
    action: {
        active: "#FFDA61", // Yellow for active states
        hover: "#FFF0C1", // Light yellow for hover states
        selected: "#FFF0C1", // Light yellow for selected states
        disabled: "#E0E0E0", // Light gray for disabled states
        disabledBackground: "#F5F5F5", // Very light gray for disabled backgrounds
    },
    // Standard colors
    error: {
        main: "#FF5252", // Red for errors
        light: "#FFCDD2", // Light red for error backgrounds
        dark: "#D32F2F", // Dark red for error text
    },
    warning: {
        main: "#FFA000", // Amber for warnings
        light: "#FFECB3", // Light amber for warning backgrounds
        dark: "#FF8F00", // Dark amber for warning text
    },
    info: {
        main: "#2196F3", // Blue for information
        light: "#BBDEFB", // Light blue for info backgrounds
        dark: "#1976D2", // Dark blue for info text
    },
    success: {
        main: "#4CAF50", // Green for success
        light: "#C8E6C9", // Light green for success backgrounds
        dark: "#388E3C", // Dark green for success text
    },
};

// Create the web theme
const webTheme = createTheme({
    palette: {
        primary: colors.primary,
        secondary: colors.secondary,
        text: colors.text,
        background: colors.background,
        error: colors.error,
        warning: colors.warning,
        info: colors.info,
        success: colors.success,
        divider: colors.divider,
        action: colors.action,
    },
    // Typography customization
    typography: {
        fontFamily: '"Quicksand", "Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontFamily: '"Quicksand", sans-serif',
            fontWeight: 700,
            color: colors.text.primary,
        },
        h2: {
            fontFamily: '"Quicksand", sans-serif',
            fontWeight: 600,
            color: colors.text.primary,
        },
        h3: {
            fontFamily: '"Quicksand", sans-serif',
            fontWeight: 600,
            color: colors.text.primary,
        },
        h4: {
            fontFamily: '"Quicksand", sans-serif',
            fontWeight: 500,
            color: colors.text.primary,
        },
        h5: {
            fontFamily: '"Quicksand", sans-serif',
            fontWeight: 500,
            color: colors.text.primary,
        },
        h6: {
            fontFamily: '"Quicksand", sans-serif',
            fontWeight: 500,
            color: colors.text.primary,
        },
        subtitle1: {
            fontFamily: '"Quicksand", sans-serif',
            color: colors.text.secondary,
        },
        subtitle2: {
            fontFamily: '"Quicksand", sans-serif',
            color: colors.text.secondary,
        },
        body1: {
            fontFamily: '"Quicksand", sans-serif',
            color: colors.text.secondary,
        },
        body2: {
            fontFamily: '"Quicksand", sans-serif',
            color: colors.text.secondary,
        },
        button: {
            fontFamily: '"Quicksand", sans-serif',
            textTransform: "none",
            fontWeight: 500,
        },
    },
    // Component Overrides
    components: {
        MuiCssBaseline: {
            styleOverrides: `
        @import url('https://fonts.googleapis.com/css2?family=Quicksand:wght@300;400;500;600;700&display=swap');
      `,
        },
        MuiButton: {
            styleOverrides: {
                root: {
                    borderRadius: 4,
                    padding: "8px 16px",
                    fontFamily: '"Quicksand", sans-serif',
                },
                containedPrimary: {
                    backgroundColor: colors.primary.main,
                    color: colors.primary.contrastText,
                    "&:hover": {
                        backgroundColor: colors.primary.dark,
                    },
                },
                containedSecondary: {
                    backgroundColor: colors.secondary.main,
                    color: colors.secondary.contrastText,
                    "&:hover": {
                        backgroundColor: colors.secondary.light,
                    },
                },
                outlinedPrimary: {
                    borderColor: colors.primary.main,
                    color: colors.primary.main,
                },
                outlinedSecondary: {
                    borderColor: colors.secondary.main,
                    color: colors.secondary.main,
                },
            },
        },
        MuiAppBar: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.secondary.main,
                    color: colors.secondary.contrastText,
                },
            },
        },
        MuiCard: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.background.paper,
                    boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                },
            },
        },
        MuiChip: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.background.paper,
                    fontFamily: '"Quicksand", sans-serif',
                },
                colorPrimary: {
                    backgroundColor: colors.primary.light,
                    color: colors.primary.contrastText,
                },
                colorSecondary: {
                    backgroundColor: colors.secondary.main,
                    color: colors.secondary.contrastText,
                },
            },
        },
        MuiIconButton: {
            styleOverrides: {
                root: {
                    color: colors.text.primary,
                    "&:hover": {
                        backgroundColor: "rgba(255, 218, 97, 0.08)",
                    },
                },
            },
        },
        MuiLink: {
            styleOverrides: {
                root: {
                    color: colors.primary.main,
                    fontFamily: '"Quicksand", sans-serif',
                    "&:hover": {
                        color: colors.primary.dark,
                    },
                },
            },
        },
        MuiDivider: {
            styleOverrides: {
                root: {
                    backgroundColor: colors.divider,
                },
            },
        },
        MuiTextField: {
            styleOverrides: {
                root: {
                    "& label": {
                        fontFamily: '"Quicksand", sans-serif',
                    },
                    "& input": {
                        fontFamily: '"Quicksand", sans-serif',
                    },
                    "& label.Mui-focused": {
                        color: colors.primary.main,
                    },
                    "& .MuiOutlinedInput-root": {
                        "&.Mui-focused fieldset": {
                            borderColor: colors.primary.main,
                        },
                    },
                },
            },
        },
        MuiCheckbox: {
            styleOverrides: {
                root: {
                    color: colors.text.secondary,
                    "&.Mui-checked": {
                        color: colors.primary.main,
                    },
                },
            },
        },
        MuiRadio: {
            styleOverrides: {
                root: {
                    color: colors.text.secondary,
                    "&.Mui-checked": {
                        color: colors.primary.main,
                    },
                },
            },
        },
        MuiSwitch: {
            styleOverrides: {
                root: {
                    "& .MuiSwitch-switchBase.Mui-checked": {
                        color: colors.primary.main,
                        "& + .MuiSwitch-track": {
                            backgroundColor: colors.primary.main,
                        },
                    },
                },
            },
        },
        MuiTypography: {
            styleOverrides: {
                root: {
                    fontFamily: '"Quicksand", sans-serif',
                },
            },
        },
        MuiListItemText: {
            styleOverrides: {
                primary: {
                    fontFamily: '"Quicksand", sans-serif',
                },
                secondary: {
                    fontFamily: '"Quicksand", sans-serif',
                },
            },
        },
    },
});

// Export colors object separately for direct access to colors
export { colors };

// Export theme as default
export default webTheme;
